import { useEffect, useState } from "react";
import { api, apiError, money } from "@/lib/api";
import { payWithProvider } from "@/lib/payments";
import { useAuth } from "@/context/AuthContext";
import { Loader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Star, Crown, Download, Receipt } from "lucide-react";
import { cn } from "@/lib/utils";
import { fileUrl } from "@/lib/api";

export default function RetailerSubscription() {
  const { refresh } = useAuth();
  const [plans, setPlans] = useState(null);
  const [me, setMe] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => {
    api.get("/plans").then((r) => setPlans(r.data.filter((p) => p.segment === "retailer")));
    api.get("/subscription/me").then((r) => setMe(r.data)).catch(() => setMe({}));
    api.get("/subscription/history").then((r) => setHistory(r.data)).catch(() => setHistory([]));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) return;
    let n = 0;
    const poll = async () => {
      n += 1;
      try { const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { toast.success("Pro activated"); await refresh(); load(); window.history.replaceState({}, "", "/retailer/subscription"); return; } } catch {}
      if (n < 10) setTimeout(poll, 2000);
    };
    poll();
  }, [refresh]);

  if (!plans || !me) return <Loader />;
  const current = me.plan_id || "ret_free";

  async function choose(p) {
    setBusy(p.id);
    try {
      if (p.price > 0) {
        const { data } = await api.post("/payments/retailer-premium", { origin_url: window.location.origin });
        await payWithProvider(data, api, { onSuccess: async () => { toast.success("Pro activated"); await refresh(); load(); }, onError: (m) => toast.error(m) });
      } else {
        await api.post("/subscription/cancel"); toast.success("Switched to Free"); await refresh(); load();
      }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(null); }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-forest">My Plan</h1>
        <p className="text-sm text-ink/60">Current plan: <b className="text-forest">{plans.find((p) => p.id === current)?.name || "Free"}</b></p>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        {plans.sort((a, b) => a.order - b.order).map((p) => (
          <div key={p.id} data-testid={`rplan-${p.id}`} className={cn("relative flex flex-col rounded-xl border bg-card p-6 shadow-sm",
            current === p.id ? "border-2 border-forest" : "border-border")}>
            {p.recommended && <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-lime px-2 py-0.5 text-xs font-semibold text-ink"><Star className="h-3 w-3" /> Popular</span>}
            <p className="font-heading text-lg font-semibold text-forest">{p.name}</p>
            <div className="mt-2 num text-3xl font-semibold text-forest">{p.price === 0 ? "₹0" : money(p.price).replace(".00", "")}<span className="text-sm font-normal text-ink/50">/mo</span></div>
            <ul className="mt-4 flex-1 space-y-2 text-sm">
              {p.features.map((f) => <li key={f} className="flex items-start gap-2 text-ink/70"><Check className="mt-0.5 h-4 w-4 shrink-0 text-olive" /> {f}</li>)}
            </ul>
            <div className="mt-5">
              {current === p.id ? <Button className="w-full bg-psage text-forest" disabled>Current plan</Button> : (
                <Button data-testid={`rchoose-${p.id}`} className={cn("w-full", p.price > 0 ? "bg-forest text-white hover:bg-forest/90" : "border border-forest bg-white text-forest hover:bg-psage")}
                  disabled={busy === p.id} onClick={() => choose(p)}>
                  {p.price > 0 ? <><Crown className="mr-2 h-4 w-4" /> Upgrade to Pro</> : "Downgrade to Free"}
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div>
        <h2 className="font-heading text-lg font-semibold text-forest">Billing history</h2>
        {history.length === 0 ? <div className="mt-3"><EmptyState title="No payments yet" hint="Your subscription invoices will appear here." icon={Receipt} /></div> : (
          <div className="mt-3 overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-psage/40 text-left"><tr className="overline text-forest"><th className="px-4 py-3">Date</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Amount</th><th className="px-4 py-3">Invoice</th></tr></thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} data-testid={`bill-${h.id}`} className="border-t border-border">
                    <td className="px-4 py-3 num text-ink/70">{(h.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3">{h.plan_name}</td>
                    <td className="px-4 py-3 num text-forest">{money(h.amount)}</td>
                    <td className="px-4 py-3">{h.invoice_id ? <a data-testid={`bill-inv-${h.id}`} href={fileUrl(`/api/invoices/${h.invoice_id}/pdf`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-forest hover:underline"><Download className="h-4 w-4" /> PDF</a> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
